import { useState } from 'react';
import { SpaceBetween, FormField, Multiselect, SelectProps } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { OptionalLabel } from './Common';
import { loadCauseSuggestions } from '../../../../hooks/causes';

import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from '../../../../hooks/features';
import { DropdownStatusProps } from '@amzn/awsui-components-react/polaris/internal/components/dropdown-status';
import { CommunityOrganization, EventInput } from '@amzn/red-velvet-api';

// Constants for input validation
const MIN_CHARACTERS_FOR_AUTOCOMPLETE_INPUT = 2;
const MAX_CHARACTERS_FOR_AUTOCOMPLETE_INPUT = 180;

export interface CommunityOrganizationComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  isReadOnly?: boolean;
}

export function CommunityOrganizationComponent(props: CommunityOrganizationComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [communityOrg, setCommunityOrg] = useState<readonly OptionDefinition[]>(
    props.currentFields.communityOrganizations?.map((org) => {
      return {
        value: org.causeId ?? org.name,
        label: org.name,
      };
    }) ?? []
  );
  const [options, setOptions] = useState<SelectProps.Options>([]);
  const [status, setStatus] = useState<DropdownStatusProps.StatusType>();

  const loadCauseOptions = async (filteringText: string) => {
    //Cause autocomplete api only loads with 3+ characters and limit of 180 characters
    if (
      filteringText.length > MIN_CHARACTERS_FOR_AUTOCOMPLETE_INPUT &&
      filteringText.length <= MAX_CHARACTERS_FOR_AUTOCOMPLETE_INPUT
    ) {
      try {
        setStatus('loading');
        setOptions([]);
        const causeOptions = await loadCauseSuggestions(
          filteringText,
          getEnabledFeatures(features, searchParams)
        );
        setOptions(causeOptions);
        setStatus('finished');
      } catch (e) {
        setStatus('error');
      }
    } else {
      setOptions([]);
    }
  };

  return (
    <FormField
      label={
        <SpaceBetween direction="horizontal" size="l">
          <OptionalLabel>{t('eventDetailsStep.communityOrganization')}</OptionalLabel>
        </SpaceBetween>
      }
      description={t('eventDetailsStep.communityOrganizationSubText')}
    >
      <Multiselect
        selectedOptions={communityOrg}
        filteringType="manual"
        onChange={(event) => {
          setCommunityOrg(event.detail.selectedOptions);
          props.updateFieldHandler({
            ...props.currentFields,
            communityOrganizations: event.detail.selectedOptions.map((opt) => {
              return {
                name: opt.label ?? opt.value,
                causeId: opt.value,
              } as CommunityOrganization;
            }),
          });
        }}
        onLoadItems={(event) => {
          loadCauseOptions(event.detail.filteringText);
        }}
        options={options}
        statusType={status}
        placeholder={t('eventDetailsStep.communityOrganizationPlaceholder')}
        empty={t('additionalInfo.noCauseSuggestions')}
        loadingText={t('additionalInfo.loadingCauseSuggestions')}
        readOnly={props.isReadOnly}
      />
    </FormField>
  );
}
